import React from 'react';
import { Crown, Award } from 'lucide-react';

/**
 * Season league table. Entries arrive sorted by total points; ties share a rank.
 *
 * Points: 1 per correct pick, 10 for winning a game outright, 4 for a split win.
 * Every current member is listed, including anyone yet to have a pick scored —
 * they sit at the bottom on zero rather than being missing, so the table always
 * matches the roster. An empty table therefore means no members at all, which in
 * practice means the fetch failed.
 */
function LeagueTable({ entries, currentUser, managerId }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="empty-state">
        <Award size={56} />
        <h3>No Table Yet</h3>
        <p>
          Everyone in the league appears here, and the points fill in as picks
          are scored.
        </p>
      </div>
    );
  }

  // Equal points share a rank — 1,2,2,4 rather than 1,2,3,4.
  let lastPoints = null;
  let lastRank = 0;
  const ranked = entries.map((entry, index) => {
    const rank = entry.totalPoints === lastPoints ? lastRank : index + 1;
    lastPoints = entry.totalPoints;
    lastRank = rank;
    return { ...entry, rank };
  });

  return (
    <>
      <div className="league-table-scroll">
        <table className="league-table">
          <thead>
            <tr>
              <th className="lt-rank">#</th>
              <th className="lt-player">Player</th>
              <th title="Points">Pts</th>
              <th title="Correct picks">Correct</th>
              <th className="lt-optional" title="Gameweeks played">Played</th>
              <th className="lt-optional" title="Percentage of picks that won">Win %</th>
              <th className="lt-optional" title="Games won outright">Won</th>
              <th className="lt-optional" title="Games won as a shared prize">Split</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((entry) => {
              const isYou = String(entry.playerId) === String(currentUser?.id);
              return (
                <tr key={entry.playerId} className={isYou ? 'lt-you' : undefined}>
                  <td className="lt-rank">{entry.rank}</td>
                  <td className="lt-player">
                    <span className="lt-name">{entry.playerName}</span>
                    {String(entry.playerId) === String(managerId) && (
                      <Crown size={13} className="lt-crown" title="League manager" />
                    )}
                    {isYou && <span className="lt-you-tag">You</span>}
                  </td>
                  <td className="lt-points">{entry.totalPoints}</td>
                  <td>{entry.correctPicks}</td>
                  <td className="lt-optional">{entry.matchesPlayed}</td>
                  <td className="lt-optional">{Math.round(entry.winPercentage)}%</td>
                  <td className="lt-optional">{entry.gamesWon}</td>
                  <td className="lt-optional">{entry.gamesSplitWon}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="helper-text league-table-key">
        Points: 1 per correct pick · 10 for winning a game · 4 for a shared win
      </p>
    </>
  );
}

export default LeagueTable;
